export const LOCALES = ["en", "es", "de", "fr", "it", "ru", "pt-br", "ar"] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export const LOCALE_META: Record<
  Locale,
  { label: string; english: string; htmlLang: string; dir: "ltr" | "rtl"; intl: string }
> = {
  en: { label: "English", english: "English", htmlLang: "en", dir: "ltr", intl: "en-US" },
  es: { label: "Español", english: "Spanish", htmlLang: "es", dir: "ltr", intl: "es-ES" },
  de: { label: "Deutsch", english: "German", htmlLang: "de", dir: "ltr", intl: "de-DE" },
  fr: { label: "Français", english: "French", htmlLang: "fr", dir: "ltr", intl: "fr-FR" },
  it: { label: "Italiano", english: "Italian", htmlLang: "it", dir: "ltr", intl: "it-IT" },
  ru: { label: "Русский", english: "Russian", htmlLang: "ru", dir: "ltr", intl: "ru-RU" },
  "pt-br": {
    label: "Português (BR)",
    english: "Brazilian Portuguese",
    htmlLang: "pt-BR",
    dir: "ltr",
    intl: "pt-BR",
  },
  ar: { label: "العربية", english: "Arabic", htmlLang: "ar", dir: "rtl", intl: "ar" },
};

export const LOCALE_COOKIE = "behot_locale";

export function isLocale(value: string | undefined | null): value is Locale {
  return !!value && (LOCALES as readonly string[]).includes(value.toLowerCase());
}

/** Locale encoded in the first path segment, or null when the path is the English default. */
export function localeFromPathname(pathname: string): Locale | null {
  const first = pathname.split("/").filter(Boolean)[0]?.toLowerCase();
  if (first && first !== "en" && isLocale(first)) return first;
  return null;
}

/** Base path for the router: "/es", "/pt-br" … or "" for English. */
export function basepathForPathname(pathname: string): string {
  const locale = localeFromPathname(pathname);
  return locale ? `/${locale}` : "";
}

/** Path without any locale prefix, always starting with "/". */
export function stripLocale(pathname: string): string {
  const locale = localeFromPathname(pathname);
  if (!locale) return pathname || "/";
  const rest = pathname.slice(locale.length + 1);
  return rest.startsWith("/") ? rest : `/${rest}`;
}

/** Same path under a different locale. */
export function pathForLocale(pathname: string, locale: Locale): string {
  const clean = stripLocale(pathname);
  const normalized = clean === "/" ? "" : clean.replace(/\/$/, "");
  return locale === DEFAULT_LOCALE ? normalized || "/" : `/${locale}${normalized}`;
}

/** Best locale from an Accept-Language header. */
export function localeFromAcceptLanguage(header: string | null | undefined): Locale {
  if (!header) return DEFAULT_LOCALE;
  for (const part of header.split(",")) {
    const tag = part.split(";")[0]?.trim().toLowerCase();
    if (!tag) continue;
    if (isLocale(tag)) return tag as Locale;
    if (tag.startsWith("pt")) return "pt-br";
    const base = tag.split("-")[0];
    if (isLocale(base)) return base as Locale;
  }
  return DEFAULT_LOCALE;
}
